/**
 * The Tender tab is the question list. Each question is one line: number,
 * title (greyed when its answer isn't in the document) and a status dot named
 * on hover. Hovering a row outlines its answer in Word; clicking opens the
 * question sheet, where drafting, status and sync live. The tender's summary,
 * criteria and win themes sit behind "About this tender".
 */
import { CheckIcon, ChevronRightIcon, XIcon } from "@/design/icons";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { Button } from "@/design/ui/button";
import { Skeleton } from "@/design/ui/skeleton";
import { GroupLabel } from "@/design/Row";

import type { TenderDetails } from "../tender/api";
import type { Question } from "../tender/questionsApi";
import type { PullProgress, QuestionsApi } from "../tender/useQuestions";
import { StatusPip } from "./StatusMenu";
import { NUMBER_CLASS, numberColumnStyle } from "./numberColumn";
import { TenderAboutSheet, dateLabel } from "./TenderAboutSheet";

export type TenderTabProps = {
  tender: TenderDetails | null;
  tenderLoading: boolean;
  tenderError: string | null;
  onRetry: () => void;
  q: QuestionsApi;
  /** A note shown above the questions (older Word, or a document linked to another bid). */
  banner?: ReactNode;
  onOpenQuestion: (question: Question) => void;
  onHover: (sectionId: string | null) => void;
  onPushAll: () => void;
  onPullAll: () => void;
  /** Pinned under the list (the suggestions bar). */
  footer?: ReactNode;
  /** A pull in progress, and its result once done (stays until dismissed). */
  pullProgress?: PullProgress | null;
  pullResult?: string | null;
  onDismissPullResult?: () => void;
};

export const TenderTab: FC<TenderTabProps> = ({
  tender,
  tenderLoading,
  tenderError,
  onRetry,
  q,
  banner,
  onOpenQuestion,
  onHover,
  onPushAll,
  onPullAll,
  footer,
  pullProgress,
  pullResult,
  onDismissPullResult,
}) => {
  const [aboutOpen, setAboutOpen] = useState(false);

  if (!tender && tenderLoading) return <TenderSkeleton />;
  if (!tender) {
    return (
      <div className="p-3">
        <div className="rounded-md border border-line bg-subtle p-3 text-sm">
          <div className="font-medium">Couldn&apos;t load the tender</div>
          <div className="mt-0.5 text-fg-3" title={tenderError ?? undefined}>
            Check your connection, then try again.
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const deadline = dateLabel(tender.submission_deadline);
  const questions = q.questions;
  const inDocument = questions.filter((x) => q.blocks.has(x.section_id)).length;
  const numberStyle = numberColumnStyle(questions.map((x) => x.section_number));

  return (
    <div className="flex h-full flex-col">
      <div className="relative min-h-0 flex-1">
        <div className="h-full overflow-y-auto pb-4">
          <button
            type="button"
            onClick={() => setAboutOpen(true)}
            className="flex w-full items-center gap-2 border-b border-line/70 px-4 py-2.5 text-left transition-colors duration-fast hover:bg-subtle"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">About this tender</span>
              <span className="block truncate text-xs text-fg-3">
                {[deadline && `Due ${deadline}`, tender.client_name].filter(Boolean).join(" · ") ||
                  "Summary, criteria and win themes"}
              </span>
            </span>
            <ChevronRightIcon className="size-3.5 shrink-0 text-fg-4" />
          </button>

          {banner}

          {pullProgress && (
            <div className="mx-3 mt-2 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs text-fg-2">
              <div className="flex items-center gap-2">
                <span className="size-3 shrink-0 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
                <span className="min-w-0 flex-1 truncate">
                  {pullProgress.stage === "fetching"
                    ? `Fetching ${pullProgress.total} answer${pullProgress.total === 1 ? "" : "s"} from mytender…`
                    : pullProgress.stage === "writing"
                      ? `Writing into the document…`
                      : `Checking what landed…`}
                </span>
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-line">
                <div
                  className="h-full bg-accent transition-[width] duration-fast"
                  style={{ width: `${Math.round((100 * pullProgress.done) / Math.max(1, pullProgress.total))}%` }}
                />
              </div>
            </div>
          )}
          {pullResult && !pullProgress && (
            <div
              role="status"
              className="mx-3 mt-2 flex items-start gap-2 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs leading-[17px] text-fg-2"
            >
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" />
              <span className="min-w-0 flex-1">{pullResult}</span>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={onDismissPullResult}
                className="shrink-0 text-fg-3 hover:text-fg"
              >
                <XIcon className="size-3.5" />
              </button>
            </div>
          )}

          <GroupLabel
            className="mt-1"
            trailing={
              questions.length > 0 && (
                <span className="flex gap-3.5">
                  <button
                    type="button"
                    onClick={onPullAll}
                    className="text-xs font-semibold text-accent-hover hover:underline"
                  >
                    Get from mytender
                  </button>
                  <button
                    type="button"
                    onClick={onPushAll}
                    className="text-xs font-semibold text-accent-hover hover:underline"
                  >
                    Send to mytender
                  </button>
                </span>
              )
            }
          >
            Questions
            {questions.length ? (
              <span className="font-normal text-fg-4">
                {" "}
                · {inDocument} of {questions.length} in document
              </span>
            ) : null}
          </GroupLabel>

          {q.loading && !questions.length ? (
            <div className="flex flex-col gap-3 px-4 py-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-3.5 w-2/3" />
              ))}
            </div>
          ) : q.error && !questions.length ? (
            <div className="mx-3 my-2 rounded-sm border border-danger/30 bg-danger-soft px-2.5 py-2 text-xs text-danger">
              {q.error} Your document is fine.{" "}
              <button type="button" className="font-semibold underline" onClick={() => void q.refresh()}>
                Try again
              </button>
            </div>
          ) : !questions.length ? (
            <div className="px-4 py-4 text-sm text-fg-3">
              No questions yet. Build the outline in mytender, or ask io.ai to extract the questions from the tender
              documents.
            </div>
          ) : (
            <div>
              {questions.map((question) => {
                const id = question.section_id;
                const inDoc = q.blocks.has(id);
                const title = question.heading || question.question || "Untitled question";
                return (
                  <button
                    key={id}
                    type="button"
                    onMouseEnter={() => onHover(id)}
                    onMouseLeave={() => onHover(null)}
                    onFocus={() => onHover(id)}
                    onBlur={() => onHover(null)}
                    onClick={() => onOpenQuestion(question)}
                    title={inDoc ? undefined : "Not in the document yet"}
                    className="flex h-9 w-full items-center gap-2.5 px-3 text-left outline-none transition-colors duration-fast hover:bg-subtle focus-visible:bg-subtle"
                  >
                    <span className={NUMBER_CLASS} style={numberStyle} title={question.section_number}>
                      {question.section_number || "·"}
                    </span>
                    <span className={cn("min-w-0 flex-1 truncate", inDoc ? "font-medium" : "text-fg-3")}>{title}</span>
                    {pullProgress?.doneIds.includes(id) ? (
                      <span className="flex size-5 shrink-0 items-center justify-center text-status-complete">
                        <CheckIcon className="size-3.5" />
                      </span>
                    ) : (
                      <StatusPip status={question.status} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {footer && <div className="shrink-0 border-t border-line px-3 py-2">{footer}</div>}

      <TenderAboutSheet open={aboutOpen} tender={tender} onClose={() => setAboutOpen(false)} />
    </div>
  );
};

export const Toast: FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.focus(), [text]);
  return (
    <div
      ref={ref}
      role="status"
      className="flex items-start gap-2 rounded-md bg-fg px-3 py-2 text-xs leading-[17px] text-fg-inverse shadow-pop animate-slide-up"
    >
      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" />
      {text}
    </div>
  );
};

const TenderSkeleton: FC = () => (
  <div className="flex flex-col gap-3 p-4">
    <Skeleton className="h-5 w-3/4" />
    <Skeleton className="h-3.5 w-1/2" />
    <Skeleton className="mt-3 h-3 w-16" />
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="flex flex-col gap-1.5 py-1">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    ))}
  </div>
);
